import React, { useState } from "react";
import { motion } from "framer-motion";
import { CHAPTERS } from "../../lib/content";
import ChapterCarousel from "./ChapterCarousel";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";

const BOOK_IMG = "https://customer-assets.emergentagent.com/job_crash-proof-movement/artifacts/5kbvkhoq_Created.png.PNG";

export default function BookSection() {
  const [previewOpen, setPreviewOpen] = useState(false);
  return (
    <section id="book" data-testid="book-section" className="relative overflow-hidden bg-ink-900 py-28 md:py-36">
      <div className="pointer-events-none absolute -left-32 top-1/4 h-72 w-72 rounded-full bg-gold/10 blur-[120px]" />
      <div className="pointer-events-none absolute -right-32 bottom-1/4 h-72 w-72 rounded-full bg-gold/5 blur-[140px]" />

      <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="relative flex items-center justify-center lg:col-span-5"
        >
          <div className="book-tilt">
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="book-tilt-inner relative w-[260px] md:w-[340px]"
            >
              <div className="book-spine" />
              <img
                src={BOOK_IMG}
                alt="CA$H OUT NEVER CRASH OUT book cover"
                className="relative w-full rounded-md shadow-[0_50px_120px_-20px_rgba(0,0,0,0.9)]"
              />
              <div className="absolute inset-y-0 left-0 w-2 rounded-l-md bg-gradient-to-r from-black to-transparent" />
            </motion.div>
          </div>
          <div className="pointer-events-none absolute -bottom-16 left-1/2 h-20 w-3/4 -translate-x-1/2 rounded-[50%] bg-gold/10 blur-3xl" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7"
        >
          <div className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold">
            <span className="h-px w-10 bg-gold/60" />
            The Book
          </div>
          <h2 className="font-display text-4xl font-bold leading-[1.05] text-white md:text-5xl lg:text-6xl">
            CA$H OUT
            <span className="block text-gradient-gold">NEVER CRASH OUT.</span>
          </h2>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-zinc-300">
            Fifteen chapters. One blueprint. Built for the young person who's tired of crashing
            out, and the leader ready to help them choose differently.
          </p>

          <div className="mt-10 grid gap-3 sm:grid-cols-2">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.03 * i }}
                className="group flex items-start gap-4 rounded-xl border border-white/5 bg-ink-700/50 p-4 transition-all hover:border-gold/40 hover:bg-ink-700"
              >
                <div className="font-display text-2xl text-gold/70 group-hover:text-gold shrink-0">
                  {c.n}
                </div>
                <div className="min-w-0">
                  <div className="font-display text-base font-semibold leading-snug text-white">{c.title}</div>
                  {c.quote && (
                    <div className="mt-1 font-display text-sm italic text-gold-pale">"{c.quote}"</div>
                  )}
                  <div className="mt-1 text-xs text-zinc-400">{c.note}</div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="https://a.co/d/0inHxgPY"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-cta-order"
              className="btn-gold rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-wider"
            >
              <i className="fa-brands fa-amazon mr-2" /> Order The Book
              <i className="fa-solid fa-arrow-up-right-from-square ml-2 text-[10px]" />
            </a>
            <a
              href="https://mrgreenbay.gumroad.com/l/CashOut"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-cta-pdf"
              className="btn-ghost-gold rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-wider"
            >
              <i className="fa-solid fa-file-pdf mr-2" /> Get The PDF
              <i className="fa-solid fa-arrow-up-right-from-square ml-2 text-[10px]" />
            </a>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              data-testid="book-cta-preview"
              className="btn-ghost-gold rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-wider"
            >
              <i className="fa-solid fa-book-open mr-2" /> Read Chapter 1 Preview
            </button>
            <a
              href="#contact"
              data-testid="book-cta-bulk"
              className="btn-ghost-gold rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-wider"
            >
              Bulk Orders For Schools
            </a>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">
              <i className="fa-solid fa-bolt-lightning mr-1.5 text-gold" />
              Paperback on Amazon · Instant PDF on Gumroad
            </div>
          </div>
        </motion.div>
      </div>

      {/* Chapter Posters Carousel */}
      <ChapterCarousel />

      {/* First Look Inside — Chapter 1 preview */}
      <div className="relative mx-auto mt-24 max-w-7xl px-6 md:mt-32 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="grid items-center gap-10 rounded-3xl border border-gold/20 bg-gradient-to-br from-ink-700/70 to-ink-900/90 p-6 md:p-10 lg:grid-cols-12"
        >
          <div className="lg:col-span-5">
            <div className="mb-4 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold">
              <span className="h-px w-10 bg-gold/60" />
              First Look Inside
            </div>
            <h3 className="font-display text-3xl font-bold leading-tight text-white md:text-4xl lg:text-5xl">
              Chapter 1 — <span className="text-gradient-gold">The Mirror Test.</span>
            </h3>
            <p className="mt-5 text-lg leading-snug text-zinc-200">
              "You can't lead nobody until you can lead yourself."
            </p>
            <p className="mt-4 text-sm leading-relaxed text-zinc-400">
              Self-honesty over image. Reflection before direction. The opening chapter
              sets the framework every reader uses to start choosing themselves on purpose
              — the first step into the 15-chapter blueprint.
            </p>

            <div className="mt-6 rounded-2xl border border-gold/20 bg-black/40 p-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Chapter 1 Theme</div>
              <p className="mt-1 font-display italic text-white">
                Self-honesty over image. Reflection before direction.
              </p>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 text-center text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              <div className="rounded-lg border border-white/5 bg-black/40 px-2 py-2">15 Chapters</div>
              <div className="rounded-lg border border-white/5 bg-black/40 px-2 py-2">One Blueprint</div>
              <div className="rounded-lg border border-white/5 bg-black/40 px-2 py-2">Built To Last</div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                data-testid="first-look-preview-btn"
                className="btn-gold rounded-full px-6 py-3 text-xs font-semibold uppercase tracking-wider"
              >
                <i className="fa-solid fa-book-open mr-2" /> Read The Preview
              </button>
              <a
                href="https://a.co/d/0inHxgPY"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost-gold rounded-full px-6 py-3 text-xs font-semibold uppercase tracking-wider"
              >
                <i className="fa-brands fa-amazon mr-2" /> Get The Full Book
                <i className="fa-solid fa-arrow-up-right-from-square ml-2 text-[9px]" />
              </a>
              <a
                href="https://mrgreenbay.gumroad.com/l/CashOut"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="first-look-pdf-btn"
                className="btn-ghost-gold rounded-full px-6 py-3 text-xs font-semibold uppercase tracking-wider"
              >
                <i className="fa-solid fa-file-pdf mr-2" /> Get The PDF
                <i className="fa-solid fa-arrow-up-right-from-square ml-2 text-[9px]" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="group relative overflow-hidden rounded-2xl border border-white/5 bg-black/60">
              <img
                src="https://customer-assets.emergentagent.com/job_crash-proof-movement/artifacts/a0xamucu_image.png"
                alt="Chapter 1 — The Mirror Test poster"
                className="w-full transition-transform duration-1000 group-hover:scale-[1.02]"
                data-testid="chapter-one-poster"
              />
              <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-gold/10" />
            </div>
            <div className="mt-4 flex items-center justify-between text-xs uppercase tracking-[0.2em] text-zinc-500">
              <span>Be Real. Be Ready. Lead You.</span>
              <span className="text-gold">— Mr. Green Bay</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Chapter 1 Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          data-testid="chapter-one-preview-modal"
          className="max-w-3xl border border-gold/30 bg-ink-900 p-0 text-white"
        >
          <DialogHeader className="border-b border-gold/15 px-8 pt-8 pb-5">
            <div className="mb-2 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-gold">
              <span className="h-px w-8 bg-gold/60" />
              First Look Inside · Chapter 1
            </div>
            <DialogTitle className="font-display text-3xl md:text-4xl leading-tight">
              The <span className="text-gradient-gold">Mirror Test</span>
            </DialogTitle>
            <DialogDescription className="text-sm italic text-gold-pale">
              "You can't lead nobody until you can lead yourself."
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto px-8 py-6 space-y-5 text-[15px] leading-relaxed text-zinc-200">
            <p>
              You ever look in the mirror and feel like it's staring through you? Like it knows
              everything you tried to hide — the broken promises, the empty plans, the version
              of you that keeps saying <span className="italic text-gold-pale">"I'm good"</span> when you know you ain't?
            </p>
            <p className="font-display text-lg text-white">
              That's the mirror test. And it's undefeated.
            </p>
            <p>
              <span className="font-display text-gold-pale">You can't lead nobody until you can lead yourself.</span> That's rule #1.
              Always has been.
            </p>
            <p>
              The hardest person you'll ever have to lead is the one staring back at you.
              You can fake confidence, rock designer, quote motivation like scripture —
              but the mirror don't care. It don't care about your followers, your clout,
              your shoes, or your résumé. It reflects what you really are — not what you post.
            </p>

            <div className="pt-2">
              <div className="mb-3 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-gold">
                <span className="h-px w-8 bg-gold/60" />
                The Real Mirror
              </div>
              <p>
                Most people avoid mirrors. Not the glass kind — the real one. The mirror
                inside you that shows the unfiltered truth. Because it's easier to critique
                someone else's flaws than to confront your own reflection.
              </p>
            </div>

            <p>
              It's easier to say, <span className="italic">"they fake,"</span> than to admit, <span className="italic">"I'm scared."</span>
              {" "}Easier to scroll than to sit with silence.
            </p>
            <p>
              But when the noise fades, when the likes disappear and the crowd clears out —
              can you still respect the person looking back?
            </p>
            <p className="font-display text-white">
              Not for what you have, but for what you hold down.
              <br />Not for your image<span className="text-gold-pale">…</span>
            </p>

            <div className="mt-2 rounded-xl border border-gold/20 bg-black/40 p-4 text-xs text-zinc-400">
              Excerpt from <span className="italic text-white">CA$H OUT NEVER CRASH OUT</span> by Damien Doran.
              <span className="block mt-1 text-[10px] uppercase tracking-[0.2em] text-zinc-500">This material may be protected by copyright.</span>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-gold/15 px-8 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">
              <i className="fa-solid fa-lock-open mr-1.5 text-gold" />
              14 more chapters in the full book
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href="https://a.co/d/0inHxgPY"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="preview-modal-buy-btn"
                className="btn-gold inline-flex items-center justify-center rounded-full px-6 py-3 text-xs font-semibold uppercase tracking-wider"
              >
                <i className="fa-brands fa-amazon mr-2" /> Get The Full Book
                <i className="fa-solid fa-arrow-up-right-from-square ml-2 text-[9px]" />
              </a>
              <a
                href="https://mrgreenbay.gumroad.com/l/CashOut"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="preview-modal-pdf-btn"
                className="btn-ghost-gold inline-flex items-center justify-center rounded-full px-6 py-3 text-xs font-semibold uppercase tracking-wider"
              >
                <i className="fa-solid fa-file-pdf mr-2" /> Get The PDF
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
